import { CSSProperties } from "react";

// Frontend mirror of the backend request_desk model (kept lightweight — only what
// the screens need). Keep values in sync with kazam-backend request_desk_request_model.ts.

export const RequestDeskType = {
  bug: "bug",
  new_requirement: "new_requirement",
  service_request: "service_request",
} as const;

export const RequestDeskStatus = {
  raised: "raised",
  approval_pending: "approval_pending",
  approved: "approved",
  triaged: "triaged",
  in_progress: "in_progress",
  resolved: "resolved",
  rejected: "rejected",
  closed: "closed",
} as const;

export const RequestDeskPriority = {
  low: "low",
  medium: "medium",
  high: "high",
  critical: "critical",
} as const;

export const SR_ENTITY_TYPE = {
  PO: "PO",
  SO: "SO",
  ST: "ST",
  OTHER: "OTHER",
} as const;

// Main ERP modules, mirroring the landing screen menu (ui/pages/landing_screen).
// Hardcoded for now — keep in sync when a module card is added there.
export const BUG_MODULES: string[] = [
  "Access Guide",
  "Access Management",
  "API Integration",
  "Complaints",
  "HRM",
  "Inventory Report",
  "Kazam Academy",
  "Knowledge Management",
  "Masters Data",
  "Orders",
  "Procurement",
  "Request Desk",
  "SOP",
  "Tasks Management",
  "Warehouse Management",
  "Website Content Panel",
  "Other",
];

export const ReasonForError: { value: string; label: string }[] = [
  {
    value: "Wrong item selected while creating document",
    label: "Wrong item selected while creating document",
  },
  {
    value: "Vendor sent a different item",
    label: "Vendor sent a different item",
  },
  { value: "Typo in code / value", label: "Typo in code / value" },
  {
    value: "Master data was wrong (maker/checker attributed)",
    label: "Master data was wrong (maker/checker attributed)",
  },
  { value: "Other", label: "Other" },
];

export interface AssignableDev {
  uid: string;
  name: string;
  department?: string;
  email?: string;
}

// A 403 arrives as a normal body (api_client returns response.data on 403):
// route-level denials say "Forbidden", in-controller ones set `forbidden`.
export const isForbiddenRes = (res: any): boolean =>
  res?.forbidden === true || res?.error === "Forbidden";

export interface ResponsibleParty {
  role: string;
  uid: string;
  name: string;
  at?: string;
  source_field: string;
}

export interface ServiceRequestPayload {
  entity_type: string;
  entity_id: string;
  entity_ref: string;
  change_requested: {
    field: string;
    current_value: string;
    desired_value: string;
    line_ref?: string;
  };
  change_note?: string;
  reason_for_error: string;
  reason_detail?: string;
  entity_stage: string;
  responsible_parties: ResponsibleParty[];
}

export interface RequestDeskRequest {
  id?: string;
  request_no: string;
  type: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  department?: string;
  attachments?: string[];
  bug?: {
    module: string;
    steps: string;
    expected: string;
    actual: string;
    environment?: string;
  };
  requirement?: {
    justification: string;
    outcome: string;
    desired_date?: string;
    department?: string;
  };
  service_request?: ServiceRequestPayload;
  approval_status?: string;
  assigned_to?: string;
  assigned_to_name?: string;
  resolved_by_name?: string;
  resolved_at?: string;
  resolution_note?: string;
  rejection_reason?: string;
  comments?: { by: string; by_name: string; at: string; text: string }[];
  created_by?: string;
  created_by_name?: string;
  created_at?: string;
}

export const STATUS_LABEL: Record<string, string> = {
  raised: "Raised",
  approval_pending: "Approval Pending",
  approved: "Approved",
  triaged: "Triaged",
  in_progress: "In Progress",
  resolved: "Resolved",
  rejected: "Rejected",
  closed: "Closed",
};

export const TYPE_LABEL: Record<string, string> = {
  bug: "Bug",
  new_requirement: "New Requirement",
  service_request: "Service Request",
};

// ---- Palette (mirrors incident-module-prototype-v2.html). Applied via inline
// styles because this Tailwind (v2 postcss7-compat) has no arbitrary-value utils.
export const RD = {
  accent: "#2a5fd0",
  accentStrong: "#1e4cb0",
  accentSoft: "#eaf0fc",
  accentLine: "#c6d5f4",
  green: "#1c8a3c",
  greenStrong: "#15702f",
  red: "#c23434",
  redSoft: "#fdeaea",
  redLine: "#f2c7c7",
  ink: "#16212f",
  ink2: "#4a5668",
  ink3: "#7c8798",
  surface2: "#f8f9fb",
  surface3: "#f1f3f6",
  border: "#e4e7ec",
};

type CSS = CSSProperties;

export const btnPrimary: CSS = {
  backgroundColor: RD.accent,
  color: "#fff",
  border: "1px solid transparent",
};
export const btnGreen: CSS = {
  backgroundColor: RD.green,
  color: "#fff",
  border: "1px solid transparent",
};
export const btnRed: CSS = {
  backgroundColor: "#fff",
  border: `1px solid ${RD.redLine}`,
  color: RD.red,
};
export const btnSecondary: CSS = {
  backgroundColor: "#fff",
  border: `1px solid ${RD.border}`,
  color: RD.ink2,
};
export const headingBar: CSS = { borderLeftColor: RD.accent };

// status chip inline styles (prototype st-* tokens)
export const STATUS_STYLE: Record<string, CSS> = {
  raised: { backgroundColor: "#eef1f5", color: "#5b6b80" },
  approval_pending: { backgroundColor: "#fdf3de", color: "#92600a" },
  approved: { backgroundColor: "#e0f5ee", color: "#0d7a5f" },
  triaged: { backgroundColor: "#efecfb", color: "#55449e" },
  in_progress: { backgroundColor: "#e7f0fc", color: "#1c5cab" },
  resolved: { backgroundColor: "#e5f4e5", color: "#1a7d1a" },
  rejected: { backgroundColor: "#fdeaea", color: "#b03434" },
  closed: { backgroundColor: "#eef1f5", color: "#5b6b80" },
};

export const TYPE_STYLE: Record<string, CSS> = {
  bug: { backgroundColor: "#fdeaea", color: "#b03434" },
  new_requirement: { backgroundColor: "#efecfb", color: "#55449e" },
  service_request: { backgroundColor: "#e7f0fc", color: "#1c5cab" },
};

export const PRIORITY_STYLE: Record<string, CSS> = {
  low: { color: "#7c8798" },
  medium: { color: "#92600a" },
  high: { color: "#c05a17" },
  critical: { color: "#c23434" },
};

export const statusStyle = (s: string): CSS =>
  STATUS_STYLE[s] || STATUS_STYLE.raised;
export const typeStyle = (t: string): CSS =>
  TYPE_STYLE[t] || STATUS_STYLE.raised;

// ---- ERP-176 Phase 2: automated apply-preview/apply. Mirrors
// kazam-backend src/request_desk/models/request_desk_apply_model.ts and
// request_desk_field_whitelist.ts — keep in sync.

export interface ApplyIssue {
  type: string;
  message: string;
  detail?: Record<string, any>;
}

export interface ApplyPlannedAction {
  step: string;
  collections: string[];
  summary: string;
}

export interface ApplyPreviewResult {
  ok: boolean;
  entity_type: "PO" | "SO" | "ST";
  field: string;
  line_ref?: string;
  analysis_doc_rows: string[];
  summary: string;
  blocking_errors: ApplyIssue[];
  warnings: ApplyIssue[];
  planned_actions: ApplyPlannedAction[];
  requires_input?: "cascade_pr" | "compensating_movement";
  computed_at?: string;
}

export interface ApplyOptions {
  cascade_pr?: boolean;
  compensating_movement?: { direction: "Inward" | "Outward"; qty: number };
  release_device_ids?: string[];
  allocate_device_ids?: string[];
}

// The exact fields the backend will auto-apply for a given entity type — must
// stay identical to request_desk_field_whitelist.ts's APPLY_FIELD_WHITELIST.
// Values are what change_requested.field must equal; label is display-only.
export const APPLY_FIELD_OPTIONS: Record<
  string,
  { value: string; label: string }[]
> = {
  PO: [
    { value: "item_code", label: "Item code" },
    { value: "qty", label: "Quantity" },
    { value: "price", label: "Price" },
    { value: "item_type", label: "Item type (Product / Service)" },
    { value: "project_id", label: "Project / Cost Center" },
  ],
  SO: [
    { value: "item_code", label: "Item code" },
    { value: "qty", label: "Quantity" },
    { value: "price", label: "Price" },
  ],
  ST: [
    { value: "item_code", label: "Item code" },
    { value: "qty", label: "Quantity" },
    // { value: "destination_warehouse", label: "Destination warehouse" },
  ],
};

// Plain-language label for a raw change_requested.field key — never show the
// underscored field name itself to a user.
export const FIELD_LABEL: Record<string, string> = {
  item_code: "Item code",
  qty: "Quantity",
  price: "Price",
  item_type: "Item type",
  status: "Status",
  destination_warehouse: "Destination warehouse",
  project_id: "Project / Cost Center",
};

export const fieldLabel = (field?: string): string =>
  (field && FIELD_LABEL[field]) || field || "";
